/**
 * Bonfire Club — Neon Sign Customizer
 * theme/assets/neon-customizer.js
 *
 * Data attributes on <div id="neon-customizer">:
 *   data-api-url        — Railway app base URL
 *   data-api-secret     — API_SECRET env var value
 *   data-shop           — myshopify.com domain
 *   data-fonts-base-url — Shopify CDN base URL for custom font files
 *                         (set via Liquid: {{ 'x' | asset_url | remove: 'x' }})
 */

(function () {
  'use strict';

  // ── Config from DOM ────────────────────────────────────────────────────────
  const ROOT       = document.getElementById('neon-customizer');
  if (!ROOT) return;

  const API_URL    = ROOT.dataset.apiUrl       || '';
  const API_SECRET = ROOT.dataset.apiSecret    || '';
  const SHOP       = ROOT.dataset.shop         || '';
  const FONTS_BASE = ROOT.dataset.fontsBaseUrl || '';

  if (!API_URL || !SHOP) {
    ROOT.innerHTML = '<p style="color:#ef4444;padding:24px;">Customizer config ontbreekt (data-api-url / data-shop).</p>';
    return;
  }

  // ── State ──────────────────────────────────────────────────────────────────
  let config             = null;
  let selectedText       = '';
  let selectedColor      = null;   // { name, hex, glow, rgb }
  let selectedFont       = null;   // { name, file, min_height_lowercase, min_height_uppercase, line_height }
  let selectedSize       = null;   // { label, multiplier }
  let selectedCustomWidth = null;  // number (cm) or null when preset is active
  let selectedWaterproof = false;
  let busy               = false;

  // ── Helpers ────────────────────────────────────────────────────────────────
  // Returns the CSS font-family name for a font object.
  // Google Fonts fonts use google_font as the registered family name;
  // asset fonts use name directly.
  function cssFamily(f) {
    return (f && f.google_font && f.google_font.trim()) ? f.google_font.trim() : (f ? f.name : '');
  }

  // ── Boot ───────────────────────────────────────────────────────────────────
  showLoader();
  fetchConfig();

  // ── Fetch config from Railway API ─────────────────────────────────────────
  async function fetchConfig() {
    try {
      const res = await fetch(`${API_URL}/api/config?shop=${encodeURIComponent(SHOP)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      config        = await res.json();
      selectedColor = config.colors[0] || null;
      selectedFont  = config.fonts[0]  || null;
      selectedSize  = (config.sizes && config.sizes[0]) || null;
      loadCustomFonts();
      try {
        render();
      } catch (renderErr) {
        ROOT.innerHTML = `<p style="color:#ef4444;padding:24px;">Render fout: ${renderErr.message}<br><pre style="font-size:11px;margin-top:8px;white-space:pre-wrap;">${renderErr.stack}</pre></p>`;
      }
    } catch (e) {
      ROOT.innerHTML = `<p style="color:#ef4444;padding:24px;">Kon configuratie niet laden: ${e.message}</p>`;
    }
  }

  // ── Custom font loading ───────────────────────────────────────────────────
  // Fonts with a `google_font` field are loaded from Google Fonts.
  // Fonts with only a `file` field are loaded from Shopify theme assets.
  function loadCustomFonts() {
    if (!config.fonts.length) return;

    // 1. Google Fonts — inject a single <link> for all google fonts
    const googleFonts = config.fonts.filter(f => f.google_font && f.google_font.trim());
    if (googleFonts.length) {
      const existing = document.getElementById('nc-google-fonts');
      if (existing) existing.remove();

      // Preconnect hints (only once)
      if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
        const pc1 = document.createElement('link');
        pc1.rel  = 'preconnect';
        pc1.href = 'https://fonts.googleapis.com';
        document.head.appendChild(pc1);

        const pc2 = document.createElement('link');
        pc2.rel         = 'preconnect';
        pc2.href        = 'https://fonts.gstatic.com';
        pc2.crossOrigin = '';
        document.head.appendChild(pc2);
      }

      const families = googleFonts
        .map(f => `family=${encodeURIComponent(f.google_font.trim())}`)
        .join('&');
      const link  = document.createElement('link');
      link.id     = 'nc-google-fonts';
      link.rel    = 'stylesheet';
      link.href   = `https://fonts.googleapis.com/css2?${families}&display=swap`;
      document.head.appendChild(link);
    }

    // 2. Custom file fonts — inject @font-face CSS
    // f.file should be a full CDN URL (e.g. https://cdn.shopify.com/…/assets/Font.ttf?v=…)
    // For backwards compat: if it looks like just a filename, fall back to FONTS_BASE.
    const assetFonts = config.fonts.filter(f => f.file && f.file.trim() && !(f.google_font && f.google_font.trim()));
    if (assetFonts.length) {
      const old = document.getElementById('nc-custom-fonts');
      if (old) old.remove();
      const css = assetFonts.map(f => {
        const url = f.file.trim().startsWith('http')
          ? f.file.trim()
          : (FONTS_BASE ? `${FONTS_BASE}${encodeURIComponent(f.file.trim())}` : '');
        if (!url) return '';
        return `@font-face { font-family: '${f.name}'; src: url('${url}'); font-display: swap; }`;
      }).filter(Boolean).join('\n');
      if (css) {
        const style       = document.createElement('style');
        style.id          = 'nc-custom-fonts';
        style.textContent = css;
        document.head.appendChild(style);
      }
    }

    // 3. Preload all fonts via Font Loading API so they are ready before the user clicks
    const allFonts = [...googleFonts, ...assetFonts];
    if (allFonts.length && document.fonts && document.fonts.load) {
      Promise.all(
        allFonts.map(f =>
          document.fonts.load(`16px '${cssFamily(f)}'`)
            .catch(() => console.warn(`[neon-customizer] Font "${f.name}" (${cssFamily(f)}) kon niet geladen worden.`))
        )
      ).then(() => {
        const el = document.getElementById('nc-preview-text');
        if (el && selectedFont) {
          el.style.fontFamily = `'${cssFamily(selectedFont)}', sans-serif`;
        }
      });
    }
  }

  // ── Loader ─────────────────────────────────────────────────────────────────
  function showLoader() {
    ROOT.innerHTML = `
      <div class="nc-loading">
        <div class="nc-spinner"></div>
        Configuratie laden…
      </div>`;
  }

  // ── Dimension calculation ──────────────────────────────────────────────────

  /**
   * Character-aware dimension calculation.
   * Finds the smallest letter in the text, measures its pixel height,
   * uses that as reference to convert all pixel dimensions into centimetres.
   * @param {string} text
   * @param {{name:string, min_height_lowercase:number, min_height_uppercase:number}} font
   * @param {number} multiplier
   * @returns {{ widthCm: number, heightCm: number }}
   */
  function calcDims(text, font, multiplier) {
    if (!font || !font.name) return { widthCm: 100, heightCm: 20 };

    if (!text || !text.trim()) {
      // Fallback: use font min heights directly
      const h = (font.min_height_lowercase || 6) * (multiplier || 1);
      const w = h * 3;
      return { widthCm: Math.round(w), heightCm: Math.round(h) };
    }

    const fontFamily = `'${cssFamily(font)}', sans-serif`;
    const FONT_SIZE  = 200; // px — large for accuracy

    // Helper: render a string and measure its pixel dimensions
    function measureText(str) {
      const el = document.createElement('span');
      el.style.cssText = [
        'position:absolute',
        'visibility:hidden',
        'white-space:pre',
        `font-family:${fontFamily}`,
        `font-size:${FONT_SIZE}px`,
        'line-height:normal',
        'left:-9999px',
        'top:-9999px',
      ].join(';');
      el.textContent = str;
      document.body.appendChild(el);
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      document.body.removeChild(el);
      return { w, h };
    }

    // 1. Determine reference cm height based on case
    // min_height_* values are stored in mm in the DB → divide by 10 to get cm
    const allUpper    = text.trim() === text.trim().toUpperCase() && /[A-Z]/.test(text);
    const refCmHeight = allUpper
      ? (font.min_height_uppercase || font.min_height_lowercase || 60) / 10
      : (font.min_height_lowercase || 60) / 10;

    // 2. Find the character with the smallest pixel height (most reliable reference)
    const candidates = ['o', 'e', 'a', 'u', 'i', 'n', 's', 'r'];
    const textChars  = [...new Set(text.toLowerCase().replace(/\s/g, ''))];
    const checkChars = [...candidates, ...textChars];

    let refChar = null;
    let refPxH  = Infinity;
    for (const char of checkChars) {
      if (!char.trim()) continue;
      const { h } = measureText(char);
      if (h > 0 && h < refPxH) { refPxH = h; refChar = char; }
    }

    // Fallback if no valid reference character found
    if (!refChar || refPxH <= 0) {
      const fallbackH = refCmHeight * (multiplier || 1);
      return { widthCm: Math.round(fallbackH * 3), heightCm: Math.round(fallbackH) };
    }

    // 3. Calculate ratio: cm per pixel
    const ratio = refCmHeight / refPxH;

    // 4. Measure full text — handle multiline by splitting on newlines
    const lines = text.split('\n');
    let totalPxW = 0;
    let totalPxH = 0;
    for (const line of lines) {
      const measured = measureText(line || ' ');
      if (measured.w > totalPxW) totalPxW = measured.w;
      totalPxH += measured.h;
    }

    // 5. Convert to cm and apply multiplier
    const m        = multiplier || 1;
    const heightCm = totalPxH * ratio * m;
    const widthCm  = totalPxW * ratio * m;

    return {
      widthCm:  Math.round(widthCm),
      heightCm: Math.round(heightCm),
    };
  }

  /**
   * Returns the current effective dimensions based on selected size/custom width.
   */
  function getCurrentDims() {
    if (!selectedFont || !config) return { widthCm: 100, heightCm: 20 };
    const text = selectedText.trim() || 'A';

    if (selectedCustomWidth !== null) {
      // Custom width: keep aspect ratio, derive height
      const base  = calcDims(text, selectedFont, 1);
      const ratio = base.widthCm / Math.max(base.heightCm, 1);
      return {
        widthCm:  selectedCustomWidth,
        heightCm: Math.max(1, Math.round(selectedCustomWidth / ratio)),
      };
    }

    if (selectedSize) {
      return calcDims(text, selectedFont, selectedSize.multiplier);
    }

    return { widthCm: 100, heightCm: 20 };
  }

  /**
   * Smallest preset width for the current text + font combination.
   * Used as the minimum value on the custom-size slider.
   */
  function getSmallestPresetWidth() {
    if (!config || !config.sizes || !config.sizes.length || !selectedFont) return 50;
    const text = selectedText.trim() || 'A';
    let min = Infinity;
    for (const s of config.sizes) {
      const { widthCm } = calcDims(text, selectedFont, s.multiplier);
      if (widthCm < min) min = widthCm;
    }
    return min === Infinity ? 50 : min;
  }

  // ── Price calculation ──────────────────────────────────────────────────────

  /** Returns the first size boundary that matches widthCm/heightCm. */
  function findSizeBoundary(boundaries, widthCm, heightCm) {
    for (const b of boundaries) {
      if (b.continue_to_infinity) return b;
      if (widthCm <= (b.max_width_cm || Infinity) && heightCm <= (b.max_height_cm || Infinity)) return b;
    }
    return boundaries[boundaries.length - 1] || {};
  }

  function calcPrice() {
    if (!config) return 0;
    const { widthCm, heightCm } = calcDims(
      selectedText,
      selectedFont,
      selectedSize ? selectedSize.multiplier : 1
    );
    const pricing = config.pricings && config.pricings[0];

    if (pricing && pricing.size_boundaries && pricing.size_boundaries.length) {
      const b = findSizeBoundary(pricing.size_boundaries, widthCm, heightCm);

      // Material cost
      const textLen      = selectedText.trim().length || 1;
      const materialCost = pricing.letter_pricing === 'fixed_per_letter'
        ? textLen * (pricing.cost_per_letter || 0)
        : widthCm * (b.material_price ?? 0.67);

      // Shipping cost
      const unitPrice    = b.shipping_unit_price ?? 0.035;
      const shippingCost = pricing.shipping_type === 'volumetric_cm3'
        ? (widthCm * heightCm * (pricing.box_length_cm || 20)) / (pricing.volume_divisor || 5000) * unitPrice
        : widthCm * heightCm * unitPrice;

      // Subtotal + surcharges
      let subtotal = materialCost + (b.start_price ?? 150);
      if (selectedColor && selectedColor.rgb) subtotal *= (1 + (pricing.rgb_surcharge_pct       ?? 15) / 100);
      if (selectedWaterproof)                 subtotal *= (1 + (pricing.waterproof_surcharge_pct ?? 15) / 100);

      return Math.round((subtotal + shippingCost) * 100) / 100;
    }

    // Legacy fallback (no pricing config)
    const w = widthCm;
    const h = heightCm;
    const cbm          = (w + 6) * (h + 6) * 9 / 5000;
    const shippingCost = cbm * (config.shipping_price_per_kg ?? 12);
    const materialCost = w   * (config.material_price_per_cm ?? 0.67);
    let   subtotal     = materialCost + (config.fixed_start_price ?? 150);
    if (selectedColor && selectedColor.rgb) subtotal *= (1 + (config.rgb_surcharge_pct       ?? 15) / 100);
    if (selectedWaterproof)                 subtotal *= (1 + (config.waterproof_surcharge_pct ?? 15) / 100);
    return Math.round((subtotal + shippingCost) * 100) / 100;
  }

  function fmtPrice(p) {
    return `€ ${p.toFixed(2).replace('.', ',')}`;
  }

  function getDimsLabel() {
    const { widthCm, heightCm } = calcDims(
      selectedText,
      selectedFont,
      selectedSize ? selectedSize.multiplier : 1
    );
    return `${widthCm} × ${heightCm} cm`;
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  function render() {
    const fontFamily  = selectedFont ? `'${cssFamily(selectedFont)}', sans-serif` : 'sans-serif';
    const lineHeight  = selectedFont && selectedFont.line_height && selectedFont.line_height !== 'normal'
      ? selectedFont.line_height : '';
    const previewText = selectedText.trim() || 'Jouw tekst hier';
    const isRgb       = selectedColor && selectedColor.rgb;
    const glow        = selectedColor && selectedColor.glow ? selectedColor.glow : 'none';
    const hexColor    = selectedColor ? selectedColor.hex : '#ff6b35';
    const price       = calcPrice();
    const dimsLabel   = getDimsLabel();
    const waterproofPct  = config.waterproof_surcharge_pct ?? 15;

    // Sizing config
    const customSizing   = config.custom_sizing  || {};
    const sizeLimits     = config.size_limits    || {};
    const minChars       = config.min_characters ?? 1;
    const sizes          = config.sizes          || [];
    const maxRange       = customSizing.max_range_cm || 300;
    const showCustom     = customSizing.enabled && !customSizing.hide_others;
    const onlyCustom     = customSizing.enabled && customSizing.hide_others;
    const isCustomActive = selectedCustomWidth !== null;
    const showSliderAlways = customSizing.show_input === 'always';

    // Compute dims for each preset button label
    const presetDims = sizes.map(s =>
      calcDims(selectedText.trim() || 'A', selectedFont, s.multiplier)
    );

    // Smallest preset width (slider min)
    const sliderMin = sizes.length
      ? Math.min(...presetDims.map(d => d.widthCm))
      : 50;
    const sliderVal = selectedCustomWidth !== null
      ? selectedCustomWidth
      : sliderMin;

    // Size buttons HTML
    const sizeButtonsHtml = !onlyCustom
      ? sizes.map((s, i) => {
          const d = presetDims[i];
          const active = selectedSize && selectedSize.label === s.label && !isCustomActive;
          return `
            <button class="nc-size-btn ${active ? 'active' : ''}" data-size-idx="${i}">
              <span class="nc-size-label">${escHtml(s.label)}</span>
              <span class="nc-size-dims">${d.widthCm} × ${d.heightCm} cm</span>
            </button>`;
        }).join('')
      : '';

    // Custom size button HTML
    const customBtnHtml = customSizing.enabled ? `
      <button class="nc-size-btn ${isCustomActive ? 'active' : ''}" id="nc-size-custom-btn">
        <span class="nc-size-label">${escHtml(customSizing.title || 'Zelf kiezen')}</span>
      </button>` : '';

    // Slider HTML (shown when custom selected OR show_input=always)
    const showSlider = isCustomActive || showSliderAlways;
    const sliderHtml = (customSizing.enabled && showSlider) ? `
      <div class="nc-custom-size-wrap" id="nc-custom-size-wrap">
        <input
          type="range"
          class="nc-custom-slider"
          id="nc-custom-slider"
          min="${sliderMin}"
          max="${maxRange}"
          step="1"
          value="${sliderVal}"
        />
        <div class="nc-custom-input-row">
          <input
            type="number"
            class="nc-custom-number input"
            id="nc-custom-number"
            min="${sliderMin}"
            max="${maxRange}"
            step="1"
            value="${sliderVal}"
          />
          <span class="nc-custom-unit">cm</span>
        </div>
      </div>` : '';

    // Size warning (shown if limits exceeded)
    const warningHtml = `<div class="nc-size-warning" id="nc-size-warning" style="display:none;"></div>`;

    ROOT.innerHTML = `
      <div class="nc-layout">

        <!-- Options column -->
        <div class="nc-options">

          <!-- Text -->
          <div class="nc-section">
            <div class="nc-section-title">Jouw tekst</div>
            <textarea
              class="nc-textarea"
              id="nc-text-input"
              rows="3"
              placeholder="Typ hier je tekst…&#10;(gebruik Enter voor meerdere regels)"
            >${escHtml(selectedText)}</textarea>
          </div>

          <!-- Colors -->
          <div class="nc-section">
            <div class="nc-section-title">Kleur</div>
            <div class="nc-color-grid" id="nc-color-grid">
              ${config.colors.map((c, i) => `
                <div>
                  <button
                    class="nc-color-btn ${selectedColor && selectedColor.name === c.name ? 'active' : ''}"
                    data-color-idx="${i}"
                    title="${escHtml(c.name)}"
                    style="${c.rgb ? '' : `box-shadow: 0 0 8px 2px ${c.hex}44;`}"
                  >
                    ${c.rgb
                      ? '<span class="rgb-swatch"></span>'
                      : `<span class="swatch" style="background:${c.hex};box-shadow:0 0 6px ${c.hex};"></span>`
                    }
                  </button>
                  <div class="nc-color-label">${escHtml(c.name)}</div>
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Fonts -->
          <div class="nc-section">
            <div class="nc-section-title">Lettertype</div>
            <div class="nc-font-list" id="nc-font-list">
              ${config.fonts.map((f, i) => `
                <button
                  class="nc-font-btn ${selectedFont && selectedFont.name === f.name ? 'active' : ''}"
                  data-font-idx="${i}"
                  style="font-family:'${escHtml(cssFamily(f))}',sans-serif;"
                >${escHtml(f.name)}</button>
              `).join('')}
            </div>
          </div>

          <!-- Size -->
          <div class="nc-section">
            <div class="nc-section-title">Maat</div>
            <div class="nc-size-grid" id="nc-size-grid">
              ${sizeButtonsHtml}
              ${customBtnHtml}
            </div>
            ${sliderHtml}
            ${warningHtml}
          </div>

          <!-- Waterproof -->
          <div class="nc-section">
            <div class="nc-section-title">Uitvoering</div>
            <div class="nc-toggle-row">
              <button class="nc-toggle-btn ${!selectedWaterproof ? 'active' : ''}" data-waterproof="false">
                <span class="icon">🏠</span>Standaard
              </button>
              <button class="nc-toggle-btn ${selectedWaterproof ? 'active' : ''}" data-waterproof="true">
                <span class="icon">💧</span>
                Waterdicht
                <span class="nc-badge">+${waterproofPct}%</span>
              </button>
            </div>
          </div>

          <!-- CTA -->
          <div class="nc-cta-wrap">
            <button class="nc-add-btn" id="nc-add-btn">
              In winkelwagen
            </button>
            <div id="nc-status"></div>
          </div>

        </div>

        <!-- Preview column -->
        <div class="nc-preview-col">
          <div class="nc-preview-stage">
            <div class="nc-price-mini" id="nc-price-mini">${fmtPrice(price)}</div>
            <div class="nc-dim-outer">
              <div class="nc-dim-box">
                <div
                  class="nc-preview-text ${isRgb ? 'rgb-anim' : ''}"
                  id="nc-preview-text"
                  style="
                    font-family: ${fontFamily};
                    ${lineHeight ? `line-height: ${lineHeight};` : ''}
                    ${isRgb ? '' : `color: ${hexColor}; text-shadow: ${glow};`}
                  "
                >${previewText.replace(/\n/g, '<br>')}</div>
              </div>
              <div class="nc-dim-footer">
                <span class="nc-dim-line"></span>
                <span class="nc-dim-label" id="nc-dim-size">${escHtml(dimsLabel)}</span>
                <span class="nc-dim-line"></span>
              </div>
            </div>
          </div>

          <div class="nc-preview-summary">
            <strong id="nc-sum-color">${escHtml(selectedColor ? selectedColor.name : '—')}</strong>
            &nbsp;·&nbsp;
            <strong id="nc-sum-font">${escHtml(selectedFont ? selectedFont.name : '—')}</strong>
            &nbsp;·&nbsp;
            <strong id="nc-sum-dims">${escHtml(dimsLabel)}</strong>
            &nbsp;·&nbsp;
            <strong id="nc-sum-use">${selectedWaterproof ? 'Waterdicht' : 'Standaard'}</strong>
            <div class="nc-price-display" id="nc-price">${fmtPrice(price)}</div>
          </div>
        </div>

      </div>`;

    attachListeners();
    checkSizeLimits();
    updateAddToCartState();
  }

  // ── Event listeners ────────────────────────────────────────────────────────
  function attachListeners() {
    // Text input
    document.getElementById('nc-text-input').addEventListener('input', e => {
      selectedText = e.target.value;
      updatePreview();
      updateDimsAndPrice();
    });

    // Colors
    document.querySelectorAll('[data-color-idx]').forEach(btn => {
      btn.addEventListener('click', () => {
        selectedColor = config.colors[parseInt(btn.dataset.colorIdx)];
        document.querySelectorAll('[data-color-idx]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        updatePreview();
        updatePrice();
        updateSummary();
      });
    });

    // Fonts
    document.querySelectorAll('[data-font-idx]').forEach(btn => {
      btn.addEventListener('click', () => {
        selectedFont = config.fonts[parseInt(btn.dataset.fontIdx)];
        document.querySelectorAll('[data-font-idx]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        updatePreview();
        try {
          updateDimsAndPrice();
        } catch (e) {
          console.error('[neon-customizer] updateDimsAndPrice error:', e);
        }
      });
    });

    // Preset size buttons
    document.querySelectorAll('[data-size-idx]').forEach(btn => {
      btn.addEventListener('click', () => {
        selectedSize        = config.sizes[parseInt(btn.dataset.sizeIdx)];
        selectedCustomWidth = null;
        document.querySelectorAll('[data-size-idx], #nc-size-custom-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        // Hide slider if show_input is on_select
        const cs = config.custom_sizing || {};
        if (cs.show_input !== 'always') {
          const wrap = document.getElementById('nc-custom-size-wrap');
          if (wrap) wrap.style.display = 'none';
        }
        updateSizeButtonDims();
        updateDimsAndPrice();
      });
    });

    // Custom size button
    const customBtn = document.getElementById('nc-size-custom-btn');
    if (customBtn) {
      customBtn.addEventListener('click', () => {
        const cs        = config.custom_sizing || {};
        const sliderMin = getSmallestPresetWidth();
        selectedCustomWidth = sliderMin;
        selectedSize        = null;
        document.querySelectorAll('[data-size-idx], #nc-size-custom-btn').forEach(b => b.classList.remove('active'));
        customBtn.classList.add('active');

        // Show the slider/input
        let wrap = document.getElementById('nc-custom-size-wrap');
        if (!wrap) {
          wrap = buildCustomSizeWrap(sliderMin, cs.max_range_cm || 300, sliderMin);
          document.getElementById('nc-size-grid').insertAdjacentElement('afterend', wrap);
          attachSliderListeners(wrap);
        } else {
          wrap.style.display = '';
          const slider = document.getElementById('nc-custom-slider');
          const numInput = document.getElementById('nc-custom-number');
          if (slider)   { slider.min = sliderMin; slider.value = sliderMin; }
          if (numInput) { numInput.min = sliderMin; numInput.value = sliderMin; }
        }

        updateDimsAndPrice();
      });
    }

    // Slider and number input (if already rendered)
    const existingWrap = document.getElementById('nc-custom-size-wrap');
    if (existingWrap) attachSliderListeners(existingWrap);

    // Waterproof toggle
    document.querySelectorAll('[data-waterproof]').forEach(btn => {
      btn.addEventListener('click', () => {
        selectedWaterproof = btn.dataset.waterproof === 'true';
        document.querySelectorAll('[data-waterproof]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        updatePrice();
        updateSummary();
      });
    });

    // Add to cart
    document.getElementById('nc-add-btn').addEventListener('click', handleAddToCart);
  }

  function buildCustomSizeWrap(min, max, val) {
    const wrap = document.createElement('div');
    wrap.className = 'nc-custom-size-wrap';
    wrap.id        = 'nc-custom-size-wrap';
    wrap.innerHTML = `
      <input type="range" class="nc-custom-slider" id="nc-custom-slider"
        min="${min}" max="${max}" step="1" value="${val}" />
      <div class="nc-custom-input-row">
        <input type="number" class="nc-custom-number input" id="nc-custom-number"
          min="${min}" max="${max}" step="1" value="${val}" />
        <span class="nc-custom-unit">cm</span>
      </div>`;
    return wrap;
  }

  function attachSliderListeners(wrap) {
    const slider   = wrap.querySelector('#nc-custom-slider');
    const numInput = wrap.querySelector('#nc-custom-number');
    if (!slider || !numInput) return;

    slider.addEventListener('input', () => {
      const v = parseInt(slider.value);
      numInput.value  = v;
      selectedCustomWidth = v;
      updateDimsAndPrice();
    });

    numInput.addEventListener('input', () => {
      const max = parseInt(slider.max);
      const min = parseInt(slider.min);
      let v = parseInt(numInput.value);
      if (isNaN(v)) return;
      v = Math.max(min, Math.min(max, v));
      slider.value = v;
      selectedCustomWidth = v;
      updateDimsAndPrice();
    });
  }

  // ── Live updates (no full re-render) ──────────────────────────────────────

  function updatePreview() {
    const el = document.getElementById('nc-preview-text');
    if (!el) return;

    const isRgb    = selectedColor && selectedColor.rgb;
    const glow     = selectedColor && selectedColor.glow ? selectedColor.glow : 'none';
    const hex      = selectedColor ? selectedColor.hex : '#ff6b35';
    const fontName = selectedFont ? cssFamily(selectedFont) : null;

    el.style.fontFamily = fontName ? `'${fontName}', sans-serif` : 'sans-serif';
    if (selectedFont && selectedFont.line_height && selectedFont.line_height !== 'normal') {
      el.style.lineHeight = selectedFont.line_height;
    }
    el.innerHTML = (selectedText.trim() || 'Jouw tekst hier').replace(/\n/g, '<br>');

    if (isRgb) {
      el.classList.add('rgb-anim');
      el.style.color      = '';
      el.style.textShadow = '';
    } else {
      el.classList.remove('rgb-anim');
      el.style.color      = hex;
      el.style.textShadow = glow;
    }

    // If the font file hasn't loaded yet, trigger the download now
    // and force a repaint once it's ready so the preview updates visually.
    if (fontName && document.fonts && document.fonts.load) {
      document.fonts.load(`16px '${fontName}'`).then(() => {
        const live = document.getElementById('nc-preview-text');
        if (live && selectedFont && cssFamily(selectedFont) === fontName) {
          // Toggle font-family to force a repaint in all browsers
          live.style.fontFamily = 'sans-serif';
          requestAnimationFrame(() => {
            live.style.fontFamily = `'${fontName}', sans-serif`;
          });
        }
      }).catch(() => {});
    }
  }

  /** Update all dimension-dependent elements without a full re-render. */
  function updateDimsAndPrice() {
    const label  = getDimsLabel();
    const dimEl  = document.getElementById('nc-dim-size');
    if (dimEl) dimEl.textContent = label;

    updateSizeButtonDims();
    updatePrice();
    updateSummary();
    checkSizeLimits();
    updateAddToCartState();
  }

  /** Refresh the dimensions shown on each preset size button. */
  function updateSizeButtonDims() {
    if (!config || !config.sizes || !selectedFont) return;
    const text = selectedText.trim() || 'A';
    document.querySelectorAll('[data-size-idx]').forEach(btn => {
      const s   = config.sizes[parseInt(btn.dataset.sizeIdx)];
      if (!s) return;
      const d   = calcDims(text, selectedFont, s.multiplier);
      const dimsEl = btn.querySelector('.nc-size-dims');
      if (dimsEl) dimsEl.textContent = `${d.widthCm} × ${d.heightCm} cm`;
    });
  }

  function updatePrice() {
    const formatted = fmtPrice(calcPrice());
    const el   = document.getElementById('nc-price');
    const mini = document.getElementById('nc-price-mini');
    if (el)   el.textContent   = formatted;
    if (mini) mini.textContent = formatted;
  }

  function updateSummary() {
    const colorEl = document.getElementById('nc-sum-color');
    const fontEl  = document.getElementById('nc-sum-font');
    const dimsEl  = document.getElementById('nc-sum-dims');
    const useEl   = document.getElementById('nc-sum-use');
    const dimSize = document.getElementById('nc-dim-size');
    const label   = getDimsLabel();

    if (colorEl) colorEl.textContent = selectedColor ? selectedColor.name : '—';
    if (fontEl)  fontEl.textContent  = selectedFont  ? selectedFont.name  : '—';
    if (dimsEl)  dimsEl.textContent  = label;
    if (useEl)   useEl.textContent   = selectedWaterproof ? 'Waterdicht' : 'Standaard';
    if (dimSize) dimSize.textContent = label;
  }

  /** Show warning if dims exceed size limits. */
  function checkSizeLimits() {
    const warnEl = document.getElementById('nc-size-warning');
    if (!warnEl || !config) return;

    const limits = config.size_limits || {};
    const { widthCm, heightCm } = getCurrentDims();
    const msgs = [];

    if (limits.max_width_cm  && widthCm  > limits.max_width_cm)  msgs.push(`Breedte (${widthCm} cm) overschrijdt het maximum van ${limits.max_width_cm} cm.`);
    if (limits.max_height_cm && heightCm > limits.max_height_cm) msgs.push(`Hoogte (${heightCm} cm) overschrijdt het maximum van ${limits.max_height_cm} cm.`);

    if (msgs.length) {
      warnEl.textContent    = '⚠️ ' + msgs.join(' ');
      warnEl.style.display  = '';
    } else {
      warnEl.style.display  = 'none';
    }
  }

  /** Enable/disable add-to-cart button based on min_characters. */
  function updateAddToCartState() {
    const btn = document.getElementById('nc-add-btn');
    if (!btn || !config) return;
    const minChars = config.min_characters ?? 1;
    const enough   = selectedText.trim().length >= minChars;
    btn.disabled   = !enough;
    btn.title      = enough ? '' : `Minimaal ${minChars} teken${minChars > 1 ? 's' : ''} vereist`;
  }

  // ── Preview capture ────────────────────────────────────────────────────────
  async function capturePreview() {
    try {
      const stage = document.querySelector('.nc-preview-stage');
      if (!stage) return null;
      const W = stage.offsetWidth;
      const H = stage.offsetHeight;
      if (!W || !H) return null;

      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      const scale  = 2;
      const canvas = document.createElement('canvas');
      canvas.width  = W * scale;
      canvas.height = H * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);

      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, W, H);

      const boxEl = document.querySelector('.nc-dim-box');
      if (boxEl) {
        const br = boxEl.getBoundingClientRect();
        const sr = stage.getBoundingClientRect();
        ctx.strokeStyle = 'rgba(130,252,88,0.28)';
        ctx.lineWidth   = 1.5;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(br.left - sr.left, br.top - sr.top, br.width, br.height);
        ctx.setLineDash([]);
      }

      const isRgb = selectedColor && selectedColor.rgb;
      const hex   = selectedColor ? selectedColor.hex : '#ff6b35';
      const ff    = selectedFont ? `'${cssFamily(selectedFont)}', sans-serif` : 'sans-serif';
      const lines = (selectedText.trim() || 'Jouw tekst hier').split('\n');
      const fs    = Math.max(28.8, Math.min(60.8, W * 0.045));
      const lh    = fs * 1.2;
      const startY = (H - lines.length * lh) / 2 + lh * 0.82;

      ctx.font      = `bold ${fs}px ${ff}`;
      ctx.textAlign = 'center';

      const glowColor = isRgb ? '#ff00ff' : hex;
      [[glowColor, 80], [glowColor, 45], [glowColor, 20], [glowColor, 7]].forEach(([c, b]) => {
        ctx.shadowColor = c;
        ctx.shadowBlur  = b;
        ctx.fillStyle   = isRgb ? '#ff00ff' : hex;
        lines.forEach((line, i) => ctx.fillText(line, W / 2, startY + i * lh));
      });
      ctx.shadowBlur = 0;

      const dimEl = document.getElementById('nc-dim-size');
      if (dimEl && dimEl.textContent && dimEl.textContent !== '—') {
        const label = dimEl.textContent;
        ctx.font      = 'bold 13px -apple-system, sans-serif';
        ctx.fillStyle = 'rgba(130,252,88,0.6)';
        const ly  = H - 18;
        const tw  = ctx.measureText(label).width;
        ctx.fillText(label, W / 2, ly);
        ctx.strokeStyle = 'rgba(130,252,88,0.2)';
        ctx.lineWidth   = 1;
        const gap = 8;
        [[16, W / 2 - tw / 2 - gap], [W / 2 + tw / 2 + gap, W - 16]].forEach(([x1, x2]) => {
          ctx.beginPath(); ctx.moveTo(x1, ly - 6); ctx.lineTo(x2, ly - 6); ctx.stroke();
        });
      }

      return canvas.toDataURL('image/png').split(',')[1];
    } catch (e) {
      console.warn('Preview capture failed (non-fatal):', e);
      return null;
    }
  }

  // ── Add to cart ────────────────────────────────────────────────────────────
  async function handleAddToCart() {
    if (busy) return;

    const text = selectedText.trim();
    const minChars = config.min_characters ?? 1;
    if (!text || text.length < minChars) {
      setStatus(`Vul minimaal ${minChars} teken${minChars > 1 ? 's' : ''} in.`, 'error');
      return;
    }
    if (!selectedColor || !selectedFont) {
      setStatus('Selecteer een kleur en lettertype.', 'error');
      return;
    }

    const { widthCm, heightCm } = getCurrentDims();

    busy = true;
    const btn = document.getElementById('nc-add-btn');
    btn.disabled    = true;
    btn.textContent = 'Even geduld…';
    clearStatus();

    try {
      const previewImage = await capturePreview();

      const createRes = await fetch(`${API_URL}/api/create-and-cart`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          secret:     API_SECRET,
          shop:       SHOP,
          text,
          font:       selectedFont.name,
          color:      selectedColor.name,
          widthCm,
          heightCm,
          textLength: text.length,
          waterproof: selectedWaterproof,
          previewImage: previewImage || undefined,
        }),
      });

      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${createRes.status}`);
      }

      const { variantId } = await createRes.json();

      const cartRes = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!cartRes.ok) {
        const err = await cartRes.json().catch(() => ({}));
        throw new Error(err.description || `Cart fout ${cartRes.status}`);
      }

      setStatus('✅ Toegevoegd! <a href="/cart" style="color:#22c55e;font-weight:700;">Naar winkelwagen →</a>', 'success');
      btn.textContent = 'Nog een bestellen';

    } catch (e) {
      setStatus(`❌ Fout: ${escHtml(e.message)}`, 'error');
      btn.textContent = 'In winkelwagen';
    } finally {
      btn.disabled = false;
      busy = false;
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  function setStatus(html, type) {
    const el = document.getElementById('nc-status');
    if (!el) return;
    el.innerHTML = `<div class="nc-${type === 'success' ? 'success' : 'error'}">${html}</div>`;
  }

  function clearStatus() {
    const el = document.getElementById('nc-status');
    if (el) el.innerHTML = '';
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

})();
